import { Fragment, useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { MessageCircle, Loader2, Send, ArrowLeft, Home as HomeIcon, User, Users as UsersIcon, Paperclip, FileText, Pencil, Trash2, Check, CheckCheck, X, Plus, Search, Languages, Flame, Settings, Phone } from "lucide-react";
import { formatDate, formatDateTime } from "@/lib/date";
import { toast } from "sonner";

import { tx, lz } from "@/i18n";
import { SupplyChoice } from "@/components/SupplyBlocks";
import { ChatMembersDialog } from "@/components/ChatMembersDialog";
const ROLE_NL = lz({ admin: "Administrator", host: "Host", client: "Eigenaar", cleaner: "Schoonmaak", subcontractor: "Onderhoud" });

const API_ORIGIN = process.env.REACT_APP_BACKEND_URL;
const attUrl = (att) => `${API_ORIGIN}${att.url}`;
const chanUrl = (ch) => ch.kind === "house" ? `/chat/house/${ch.id}` : ch.kind === "direct" ? `/chat/direct/${ch.id}` : `/chat/${ch.id}`;
const keyOf = (c) => `${c.kind}:${c.id}`;

const SECTIONS = lz([
  { kind: "house", label: "Woningchats", icon: HomeIcon },
  { kind: "guest", label: "Gastchats", icon: UsersIcon },
  { kind: "direct", label: "1-op-1", icon: User },
  { kind: "direct", group: true, label: "Groepen", icon: UsersIcon },
]);

const fmtTime = (iso) => formatDateTime(iso);

export default function Chat() {
  const { user } = useAuth();
  const { refresh: refreshNotif } = useNotifications();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [convos, setConvos] = useState(null);
  const [active, setActive] = useState(null); // { kind, id }
  const [thread, setThread] = useState(null);
  const [membersOpen, setMembersOpen] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [flameMinutes, setFlameMinutes] = useState(30);
  const [savingSettings, setSavingSettings] = useState(false);
  const [contacts, setContacts] = useState(null);
  const [contactSearch, setContactSearch] = useState("");
  const [convoSearch, setConvoSearch] = useState("");
  const [xlate, setXlate] = useState({});
  const [showOrig, setShowOrig] = useState({});
  const endRef = useRef(null);
  const boxRef = useRef(null);
  const seenRef = useRef({ key: null, len: 0 });
  const [unreadId, setUnreadId] = useState(null);
  const attachRef = useRef(null);
  const inputRef = useRef(null);
  const canChat = ["admin", "host", "client", "cleaner", "subcontractor"].includes(user.role);
  const isAdmin = user.role === "admin";

  // Mirror backend _chat_perm so the subtitle always matches what this user can actually see.
  const chatPerm = (kind) => {
    if (user.role === "admin") return true;
    if (user.role === "client" && kind === "guest") return false;
    const val = user[{ house: "chat_house", guest: "chat_guest", direct: "chat_direct" }[kind]];
    if (val !== null && val !== undefined) return !!val;
    if (kind === "direct") return ["host", "client", "cleaner", "subcontractor"].includes(user.role);
    return ["host", "client"].includes(user.role);
  };
  const subtitleItems = [];
  if (chatPerm("guest")) subtitleItems.push(tx("gastgesprekken"));
  if (chatPerm("house")) subtitleItems.push(tx("woninggesprekken"));
  if (chatPerm("direct")) subtitleItems.push(user.role === "client" ? tx("1-op-1 met Costa Key") : tx("1-op-1-gesprekken"));
  const joinNl = (arr) => (arr.length <= 1 ? arr[0] || "" : `${arr.slice(0, -1).join(", ")} ${tx("en")} ${arr[arr.length - 1]}`);
  const subtitle = subtitleItems.length
    ? tx("{0}{1}, overzichtelijk bij elkaar.", { 0: joinNl(subtitleItems).charAt(0).toUpperCase(), 1: joinNl(subtitleItems).slice(1) })
    : tx("Je gesprekken, overzichtelijk bij elkaar.");

  const loadConvos = () => api.get("/chat/conversations").then(({ data }) => setConvos(data)).catch(() => setConvos([]));
  const loadThread = (ch) => api.get(chanUrl(ch)).then(({ data }) => setThread({ ...data, kind: ch.kind, _key: keyOf(ch) })).catch((err) => toast.error(apiError(err)));

  useEffect(() => { if (canChat) loadConvos(); }, []);

  // Deep-link: ?res=<reservation_id> | ?house=<house_id> | ?user=<user_id>
  useEffect(() => {
    const res = searchParams.get("res"), house = searchParams.get("house"), usr = searchParams.get("user");
    let ch = null;
    if (res) ch = { kind: "guest", id: res };
    else if (house) ch = { kind: "house", id: house };
    else if (usr) ch = { kind: "direct", id: usr };
    if (ch) {
      setActive(ch);
      ["res", "house", "user"].forEach((k) => searchParams.delete(k));
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!canChat) return;
    const id = setInterval(() => { loadConvos(); refreshNotif(); }, 5000);
    return () => clearInterval(id);
  }, [canChat]);

  useEffect(() => {
    if (!active) { setThread(null); return; }
    loadThread(active);
    const id = setInterval(() => loadThread(active), 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [active && keyOf(active)]);

  // Bij openen: naar eerste ongelezen bericht, anders helemaal onderaan. Daarna: nieuwe berichten onderaan.
  useEffect(() => {
    if (!thread) { seenRef.current = { key: null, len: 0 }; setUnreadId(null); return; }
    const s = seenRef.current, len = thread.messages.length;
    if (s.key !== thread._key) {
      s.key = thread._key; s.len = len;
      const uid = thread.first_unread_id || null;
      setUnreadId(uid);
      const go = () => {
        const box = boxRef.current;
        if (!box) return;
        const el = uid && box.querySelector(`[data-testid="chat-msg-${uid}"]`);
        box.scrollTop = el ? box.scrollTop + el.getBoundingClientRect().top - box.getBoundingClientRect().top - 44 : box.scrollHeight;
      };
      requestAnimationFrame(go); setTimeout(go, 350);
      return;
    }
    if (len > s.len) { const box = boxRef.current; if (box) box.scrollTo({ top: box.scrollHeight, behavior: "smooth" }); }
    s.len = len;
  }, [thread]);

  // Compact maar uitklapbaar invoerveld: groei mee met de inhoud (tot een maximum).
  useEffect(() => {
    const el = inputRef.current;
    if (el) { el.style.height = "auto"; el.style.height = `${Math.min(el.scrollHeight, 120)}px`; }
  }, [text]);

  const send = async () => {
    const t = text.trim();
    if (!t || !active) return;
    setSending(true);
    try {
      await api.post(chanUrl(active), { text: t });
      setText("");
      await loadThread(active);
      loadConvos();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSending(false); }
  };

  const uploadAttachment = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !active) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (text.trim()) fd.append("text", text.trim());
      await api.post(`${chanUrl(active)}/attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setText("");
      await loadThread(active);
      loadConvos();
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); if (attachRef.current) attachRef.current.value = ""; }
  };

  const startEdit = (m) => { setEditingId(m.id); setEditText(m.text || ""); };
  const saveEdit = async (id) => {
    const t = editText.trim();
    if (!t) return;
    try { await api.patch(`/chat/message/${id}`, { text: t }); setEditingId(null); await loadThread(active); }
    catch (err) { toast.error(apiError(err)); }
  };
  const deleteMsg = async (id) => {
    if (!window.confirm(tx("Bericht verwijderen?"))) return;
    try { await api.delete(`/chat/message/${id}`); await loadThread(active); loadConvos(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const translateTeam = async (id) => {
    if (xlate[id]?.text) { setXlate((p) => ({ ...p, [id]: { ...p[id], show: !p[id].show } })); return; }
    setXlate((p) => ({ ...p, [id]: { ...(p[id] || {}), loading: true } }));
    try {
      const { data } = await api.patch(`/chat/message/${id}/translate`, null, { params: { target: "nl" } });
      setXlate((p) => ({ ...p, [id]: { text: data.translation, show: true, loading: false } }));
    } catch (err) { toast.error(apiError(err)); setXlate((p) => ({ ...p, [id]: { ...(p[id] || {}), loading: false } })); }
  };
  const deleteConversation = async () => {
    if (!active) return;
    if (!window.confirm(tx("Dit hele gesprek verwijderen? Alle berichten worden permanent verwijderd."))) return;
    try {
      const { data } = await api.delete(`/chat/conversation/${active.kind}/${active.id}`);
      toast.success(tx("Gesprek verwijderd ({0} bericht{1})", { 0: data.deleted, 1: data.deleted === 1 ? "" : "en" }));
      setActive(null); setThread(null); loadConvos(); refreshNotif?.();
    } catch (err) { toast.error(apiError(err)); }
  };

  const openNew = () => {
    setNewOpen(true);
    setContactSearch("");
    if (!contacts) api.get("/chat/contacts").then(({ data }) => setContacts(data)).catch((err) => toast.error(apiError(err)));
  };
  const startChat = (kind, id) => {
    setNewOpen(false);
    setActive({ kind, id });
    loadConvos();
  };
  const [groupSel, setGroupSel] = useState([]);
  const startGroup = async () => {
    try {
      const { data } = await api.post("/chat/groups", { members: groupSel });
      setGroupSel([]);
      startChat("direct", data.channel_id);
    } catch (err) { toast.error(apiError(err)); }
  };

  const openSettings = () => {
    setSettingsOpen(true);
    api.get("/chat/settings").then(({ data }) => setFlameMinutes(data.flame_minutes)).catch((err) => toast.error(apiError(err)));
  };
  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data } = await api.put("/chat/settings", { flame_minutes: Number(flameMinutes) });
      setFlameMinutes(data.flame_minutes);
      toast.success(tx("Vlam-drempel ingesteld op {0} min", { 0: data.flame_minutes }));
      setSettingsOpen(false);
      loadConvos();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSavingSettings(false); }
  };

  if (!canChat) return <div className="text-muted-foreground">{tx("Geen toegang tot de chat.")}</div>;

  const totalUnread = (convos || []).reduce((s, c) => s + (c.unread || 0), 0);
  const isMine = (m) => active?.kind === "guest" ? m.sender === "team" : m.sender_id === user.id;
  const headerTitle = !thread ? "" : (active?.kind === "guest" ? (thread.guest_name || tx("Gast")) : thread.title);
  const headerSub = !thread ? "" : (active?.kind === "guest"
    ? `${thread.house_name || ""}${thread.arrival ? ` · ${formatDate(thread.arrival)} → ${formatDate(thread.departure)}` : ""}`
    : tx(thread.subtitle));
  const HeaderIcon = active?.kind === "house" ? HomeIcon : active?.kind === "direct" && !thread?.group ? User : UsersIcon;
  const isObserving = active?.kind === "direct" && !!thread?.observing;

  const q = convoSearch.trim().toLowerCase();
  // Gastchats verdwijnen een dag na vertrek (tenzij ongelezen); bij zoeken komen ze terug.
  const todayStr = new Date().toLocaleDateString("sv-SE");
  const expired = (c) => c.kind === "guest" && !c.unread && c.departure && c.departure.slice(0, 10) < todayStr;
  const matchConvo = (c) => (q ? [c.title, c.last_text, c.subtitle].some((v) => (v || "").toLowerCase().includes(q)) : !expired(c));
  const sortConvos = (a, b) => (b.unread > 0) - (a.unread > 0) || new Date(b.last_at || 0) - new Date(a.last_at || 0);
  const anyMatch = (convos || []).some(matchConvo);

  const ConvoList = (
    <div className="bg-card rounded-none border-0 lg:rounded-xl lg:border lg:border-border overflow-hidden flex flex-col h-full w-full min-w-0" data-testid="chat-convo-list">
      <div className="px-4 py-3 border-b border-border flex items-center gap-2">
        <button className="lg:hidden -ml-1 mr-0.5 text-muted-foreground" onClick={() => navigate(-1)} data-testid="chat-exit-btn" aria-label={tx("Terug")}><ArrowLeft className="h-5 w-5" /></button>
        <MessageCircle className="h-4 w-4 text-primary" />
        <span className="font-display font-semibold">{tx("Message Center")}</span>
        {totalUnread > 0 && <span className="grid place-items-center min-w-[20px] h-5 px-1.5 rounded-full bg-destructive text-white text-[11px] font-bold" data-testid="chat-total-unread">{totalUnread}</span>}
        {isAdmin && (
          <Button size="sm" variant="outline" onClick={openSettings} className="ml-auto h-8 gap-1.5" title={tx("Vlam-drempel instellen")} data-testid="chat-settings-btn">
            <Settings className="h-4 w-4" /> {tx("Vlam")}
          </Button>
        )}
        <Button size="sm" onClick={openNew} className={`${isAdmin ? "" : "ml-auto"} h-8 bg-primary hover:bg-primary/90 gap-1.5`} data-testid="chat-new-btn">
          <Plus className="h-4 w-4" /> {tx("Nieuw")}
        </Button>
      </div>
      <div className="px-3 py-2.5 border-b border-border">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input value={convoSearch} onChange={(e) => setConvoSearch(e.target.value)} placeholder={tx("Zoek in gesprekken…")} className="pl-8 pr-8 h-9" data-testid="chat-convo-search" />
          {convoSearch && <button onClick={() => setConvoSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="chat-convo-search-clear"><X className="h-3.5 w-3.5" /></button>}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {convos === null ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : convos.length === 0 || (!q && !anyMatch) ? (
          <p className="text-sm text-muted-foreground p-6 text-center">{tx("Nog geen gesprekken. Start er een via een woning of gebruiker, of wacht op een gastbericht.")}</p>
        ) : !anyMatch ? (
          <p className="text-sm text-muted-foreground p-6 text-center" data-testid="chat-convo-no-results">{tx("Geen gesprekken gevonden voor \"")}{convoSearch}".</p>
        ) : (
          SECTIONS.map(({ kind, group, label, icon: Icon }) => {
            const list = convos.filter((c) => c.kind === kind && !!c.group === !!group && matchConvo(c)).sort(sortConvos);
            if (!list.length) return null;
            return (
              <div key={`${kind}${group ? "-group" : ""}`}>
                <div className="px-4 pt-3 pb-1 text-xs lg:text-[11px] uppercase tracking-wide font-semibold text-primary flex items-center gap-1.5"><Icon className="h-3 w-3" /> {label}</div>
                {list.map((c) => (
                  <button
                    key={keyOf(c)}
                    onClick={() => setActive({ kind: c.kind, id: c.id })}
                    data-testid={`chat-convo-${c.kind}-${c.id}`}
                    className={`w-full text-left px-4 py-3 border-b border-border hover:bg-accent transition-colors ${c.flame ? "border-l-2 border-l-orange-500 bg-orange-50/60" : ""} ${active && keyOf(active) === keyOf(c) ? "bg-accent" : ""}`}
                  >
                    <div className="flex items-center gap-2">
                      {c.flame && <Flame className="h-4 w-4 text-orange-500 animate-pulse shrink-0" data-testid={`chat-flame-${c.kind}-${c.id}`} />}
                      <span className="font-medium text-base lg:text-sm truncate flex-1">{c.title}</span>
                      {c.unread > 0 && <span className="grid place-items-center min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-white text-[10px] font-bold" data-testid={`chat-unread-${c.kind}-${c.id}`}>{c.unread}</span>}
                    </div>
                    <p className="text-sm lg:text-xs text-muted-foreground truncate mt-0.5">{c.last_text}</p>
                    <p className={`text-xs lg:text-[10px] mt-0.5 ${c.flame ? "text-orange-600 font-semibold" : "text-muted-foreground/70"}`}>{c.flame && user.role === "admin" ? tx("⏱ Wacht >30 min · ") : ""}{c.subtitle ? `${tx(c.subtitle)} · ` : ""}{fmtTime(c.last_at)}</p>
                  </button>
                ))}
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  const ThreadPane = (
    <div className="bg-card rounded-none border-0 lg:rounded-xl lg:border lg:border-border flex flex-col h-full w-full min-w-0" data-testid="chat-thread">
      {!thread ? (
        <div className="flex-1 grid place-items-center text-muted-foreground text-sm p-6 text-center">
          <div><MessageCircle className="h-10 w-10 mx-auto mb-3 opacity-30" /> {tx("Kies een gesprek om te lezen en te reageren.")}</div>
        </div>
      ) : (
        <>
          <div className="px-4 py-3 border-b border-border flex items-center gap-2">
            <button className="lg:hidden mr-1 text-muted-foreground" onClick={() => setActive(null)} data-testid="chat-back-btn"><ArrowLeft className="h-4 w-4" /></button>
            <span className="grid place-items-center h-8 w-8 rounded-full bg-secondary/15 text-secondary"><HeaderIcon className="h-4 w-4" /></span>
            <div className="min-w-0">
              <div className={`font-display font-semibold truncate ${active?.kind === "house" || (active?.kind === "direct" && !thread.observing) ? "text-base" : "text-sm"}`}>{headerTitle}</div>
              {active?.kind !== "house" && !(active?.kind === "direct" && !thread.observing) && <div className="text-xs text-muted-foreground truncate">{headerSub}</div>}
            </div>
            {thread.call_phone && active?.kind !== "house" && (
              <a href={`https://wa.me/${thread.call_phone.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer"
                className="ml-auto shrink-0 grid place-items-center h-8 w-8 rounded-full text-[#25D366] hover:bg-[#25D366]/10 transition-colors"
                title={tx("Bellen via WhatsApp")} data-testid="chat-call-whatsapp-btn"><Phone className="h-4 w-4 fill-current" /></a>
            )}
            {isAdmin && active?.kind === "direct" && thread.channel_id && (
              <button onClick={() => setMembersOpen(true)} className={`${thread.call_phone ? "" : "ml-auto"} shrink-0 text-muted-foreground hover:text-primary p-1.5 rounded-md hover:bg-primary/10 transition-colors`} title={tx("Deelnemers toevoegen")} data-testid="chat-add-members-btn">
                <UsersIcon className="h-4 w-4" />
              </button>
            )}
            {isAdmin && (
              <button onClick={deleteConversation} className={`${active?.kind === "direct" && thread.channel_id ? "" : "ml-auto"} shrink-0 text-muted-foreground hover:text-destructive p-1.5 rounded-md hover:bg-destructive/10 transition-colors`} title={tx("Gesprek verwijderen")} data-testid="chat-delete-conversation-btn">
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
          <div ref={boxRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-gradient-to-br from-primary/15 via-background to-background" data-testid="chat-messages-box">
            {thread.messages.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">{tx("Nog geen berichten in dit gesprek.")}</p>
            ) : thread.messages.map((m) => {
              const mine = isMine(m);
              const att = m.attachment;
              const senderLabel = (mine ? (m.sender_name || tx("Jij")) : (active?.kind === "guest" ? (thread.guest_name || tx("Gast")) : (m.sender_name || tx("Onbekend")))).split(" ")[0];
              return (
                <Fragment key={m.id}>
                {unreadId === m.id && (
                  <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-primary" data-testid="chat-unread-divider">
                    <span className="h-px flex-1 bg-primary/40" /> {tx("Ongelezen berichten")} <span className="h-px flex-1 bg-primary/40" />
                  </div>
                )}
                <div className={`group flex items-end gap-1.5 ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
                  {isAdmin && editingId !== m.id && (
                    <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {m.text && <button onClick={() => startEdit(m)} className="text-muted-foreground hover:text-foreground" data-testid={`chat-edit-${m.id}`}><Pencil className="h-3.5 w-3.5" /></button>}
                      <button onClick={() => deleteMsg(m.id)} className="text-muted-foreground hover:text-destructive" data-testid={`chat-delete-${m.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  )}
                  <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm ${mine ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-card border border-border rounded-bl-sm"}`}>
                    {att && (att.kind === "image" ? (
                      <a href={attUrl(att)} target="_blank" rel="noreferrer" data-testid={`chat-att-${m.id}`}>
                        <img src={`${attUrl(att)}?w=800`} loading="lazy" decoding="async" alt={att.filename} className="rounded-lg max-h-52 max-w-full mb-1 object-cover" />
                      </a>
                    ) : (
                      <a href={attUrl(att)} target="_blank" rel="noreferrer" className={`flex items-center gap-2 mb-1 underline ${mine ? "text-primary-foreground" : "text-secondary"}`} data-testid={`chat-att-${m.id}`}>
                        <FileText className="h-4 w-4 shrink-0" /> <span className="truncate">{att.filename}</span>
                      </a>
                    ))}
                    {editingId === m.id ? (
                      <div className="flex items-center gap-1.5">
                        <Input value={editText} onChange={(e) => setEditText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveEdit(m.id); }} className="h-8 text-foreground" data-testid={`chat-edit-input-${m.id}`} autoFocus />
                        <button onClick={() => saveEdit(m.id)} className="text-primary-foreground" data-testid={`chat-edit-save-${m.id}`}><Check className="h-4 w-4" /></button>
                        <button onClick={() => setEditingId(null)}><X className="h-4 w-4" /></button>
                      </div>
                    ) : (
                      m.text && <p className="whitespace-pre-line break-words text-base">{m.text}</p>
                    )}
                    {m.original_text && editingId !== m.id && (
                      <div className="mt-1" data-testid={`chat-orig-wrap-${m.id}`}>
                        <button type="button" onClick={() => setShowOrig((p) => ({ ...p, [m.id]: !p[m.id] }))} className={`inline-flex items-center gap-1 text-[11px] font-medium ${mine ? "text-primary-foreground/80 hover:text-primary-foreground" : "text-secondary hover:text-secondary/80"}`} data-testid={`chat-show-orig-${m.id}`}>
                          <Languages className="h-3 w-3" /> {showOrig[m.id] ? tx("Verberg origineel") : tx("Toon origineel")}
                        </button>
                        {showOrig[m.id] && <p className={`mt-1 pt-1 border-t whitespace-pre-line break-words italic ${mine ? "border-white/25 text-primary-foreground/90" : "border-border text-muted-foreground"}`} data-testid={`chat-orig-${m.id}`}>{m.original_text}</p>}
                      </div>
                    )}
                    {m.text && !m.original_text && editingId !== m.id && (active?.kind !== "house" || /rossinyol/i.test(thread?.title || "")) && (
                      <div className="mt-1">
                        <button type="button" onClick={() => translateTeam(m.id)} disabled={xlate[m.id]?.loading}
                          className={`inline-flex items-center gap-1 text-[11px] font-medium ${mine ? "text-primary-foreground/80 hover:text-primary-foreground" : "text-secondary hover:text-secondary/80"}`}
                          data-testid={`chat-translate-${m.id}`}>
                          {xlate[m.id]?.loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Languages className="h-3 w-3" />}
                          {xlate[m.id]?.show ? tx("Toon origineel") : tx("Vertaal (NL)")}
                        </button>
                        {xlate[m.id]?.show && xlate[m.id]?.text && (
                          <p className={`mt-1 pt-1 border-t whitespace-pre-line break-words italic ${mine ? "border-white/25 text-primary-foreground/90" : "border-border text-muted-foreground"}`} data-testid={`chat-translation-${m.id}`}>{xlate[m.id].text}</p>
                        )}
                      </div>
                    )}
                    {m.supply && <SupplyChoice m={m} canDecide={user.role === "client"} mine={mine} onDone={() => loadThread(active)} />}
                    <p className={`text-[10px] mt-1 ${mine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{senderLabel}{m.edited ? tx(" · bewerkt") : ""} · {fmtTime(m.created_at)}{mine && <span className="ml-1 align-middle" data-testid={`chat-ticks-${m.id}`} title={m.read_count > 0 ? tx("Gelezen") : tx("Verzonden")}>{m.read_count > 0 ? <CheckCheck className="h-3 w-3 inline" /> : <Check className="h-3 w-3 inline opacity-60" />}</span>}</p>
                    {isAdmin && m.read_receipts?.length > 0 && (
                      <div className={`mt-1 pt-1 border-t ${mine ? "border-white/15" : "border-border"}`} data-testid={`chat-receipts-${m.id}`}>
                        {m.read_receipts.map((r, i) => (
                          <p key={i} className={`text-[10px] ${mine ? "text-primary-foreground/60" : "text-muted-foreground/80"}`}>{tx("Gelezen door")} {(r.name || "onbekend").split(" ")[0]} op {fmtTime(r.at)}</p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                </Fragment>
              );
            })}
            <div ref={endRef} />
          </div>
          <div className="border-t border-border">
            {isObserving && (
              <p className="px-3 pt-2 text-[11px] text-amber-600" data-testid="chat-observing-note">
                {tx("Je reageert als beheerder in een gesprek tussen twee anderen — beiden zien jouw bericht.")}
              </p>
            )}
            <div className="p-3 flex items-end gap-2">
              <input ref={attachRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp,application/pdf" className="hidden" onChange={uploadAttachment} data-testid="chat-attach-input" />
              <Button type="button" variant="outline" size="icon" onClick={() => attachRef.current?.click()} disabled={uploading} className="shrink-0" data-testid="chat-attach-btn">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
              </Button>
              <Textarea
                ref={inputRef}
                rows={1}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={tx("Typ een bericht…")}
                className="min-h-10 h-10 max-h-[120px] resize-none py-2 leading-5 overflow-y-auto"
                data-testid="chat-input"
              />
              <Button onClick={send} disabled={sending || !text.trim()} className="bg-primary hover:bg-primary/90 shrink-0" data-testid="chat-send-btn">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="animate-fade-up fixed inset-0 z-50 bg-background flex flex-col lg:static lg:z-auto lg:bg-transparent lg:block">
      <div className="mb-6 hidden lg:block">
        <h1 className="font-display font-bold text-3xl tracking-tight">{tx("Message Center")}</h1>
        <p className="text-muted-foreground mt-1" data-testid="chat-subtitle">{subtitle}</p>
      </div>
      <div className="flex-1 min-h-0 flex flex-col lg:flex-none lg:grid lg:grid-cols-[340px_1fr] lg:gap-4 lg:h-[calc(100vh-220px)] lg:min-h-[480px]">
        <div className={`${active ? "hidden lg:flex" : "flex"} flex-1 min-h-0 min-w-0`}>{ConvoList}</div>
        <div className={`${active ? "flex" : "hidden lg:flex"} flex-1 min-h-0 min-w-0`}>{ThreadPane}</div>
      </div>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-md" data-testid="chat-new-dialog">
          <DialogHeader>
            <DialogTitle>{tx("Nieuw gesprek")}</DialogTitle>
            <DialogDescription>{isAdmin ? tx("Start een woningchat, een 1-op-1-gesprek of een groepsgesprek.") : tx("Start een woningchat of een 1-op-1-gesprek.")}</DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="house">
            <TabsList className={`grid ${isAdmin ? "grid-cols-3" : "grid-cols-2"} w-full`}>
              <TabsTrigger value="house" data-testid="chat-new-tab-house"><HomeIcon className="h-4 w-4 mr-1.5" /> {tx("Woningchat")}</TabsTrigger>
              <TabsTrigger value="direct" data-testid="chat-new-tab-direct"><User className="h-4 w-4 mr-1.5" /> 1-op-1</TabsTrigger>
              {isAdmin && <TabsTrigger value="group" data-testid="chat-new-tab-group"><UsersIcon className="h-4 w-4 mr-1.5" /> {tx("Groep")}</TabsTrigger>}
            </TabsList>
            <div className="mt-3">
              <Input value={contactSearch} onChange={(e) => setContactSearch(e.target.value)} placeholder={tx("Zoek…")} data-testid="chat-new-search" />
            </div>
            {contacts === null ? (
              <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : (
              <>
                <TabsContent value="house" className="mt-3">
                  <div className="max-h-[320px] overflow-y-auto -mx-1">
                    {(contacts.houses || []).filter((h) => h.name.toLowerCase().includes(contactSearch.toLowerCase())).map((h) => (
                      <button key={h.id} onClick={() => startChat("house", h.id)} data-testid={`chat-new-house-${h.id}`}
                        className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-accent transition-colors flex items-center gap-2.5">
                        <span className="grid place-items-center h-8 w-8 rounded-full bg-secondary/15 text-secondary shrink-0"><HomeIcon className="h-4 w-4" /></span>
                        <span className="font-medium text-sm truncate">{h.name}</span>
                      </button>
                    ))}
                    {(contacts.houses || []).filter((h) => h.name.toLowerCase().includes(contactSearch.toLowerCase())).length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-6">{tx("Geen woningen gevonden.")}</p>
                    )}
                  </div>
                </TabsContent>
                <TabsContent value="direct" className="mt-3">
                  <div className="max-h-[320px] overflow-y-auto -mx-1">
                    {(contacts.users || []).filter((u) => u.name.toLowerCase().includes(contactSearch.toLowerCase())).map((u) => (
                      <button key={u.id} onClick={() => startChat("direct", u.id)} data-testid={`chat-new-user-${u.id}`}
                        className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-accent transition-colors flex items-center gap-2.5">
                        <span className="grid place-items-center h-8 w-8 rounded-full bg-secondary/15 text-secondary shrink-0"><User className="h-4 w-4" /></span>
                        <span className="min-w-0">
                          <span className="block font-medium text-sm truncate">{u.name}</span>
                          <span className="block text-xs text-muted-foreground">{ROLE_NL[u.role] || u.role}</span>
                        </span>
                      </button>
                    ))}
                    {(contacts.users || []).filter((u) => u.name.toLowerCase().includes(contactSearch.toLowerCase())).length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-6">{tx("Geen gebruikers gevonden.")}</p>
                    )}
                  </div>
                </TabsContent>
                {isAdmin && (
                  <TabsContent value="group" className="mt-3">
                    <div className="max-h-[280px] overflow-y-auto -mx-1">
                      {(contacts.users || []).filter((u) => u.role !== "client" && u.name.toLowerCase().includes(contactSearch.toLowerCase())).map((u) => (
                        <label key={u.id} className="w-full px-3 py-2 rounded-lg hover:bg-accent flex items-center gap-2.5 cursor-pointer" data-testid={`chat-group-user-${u.id}`}>
                          <Checkbox checked={groupSel.includes(u.id)} onCheckedChange={(v) => setGroupSel((p) => (v ? [...p, u.id] : p.filter((x) => x !== u.id)))} />
                          <span className="min-w-0">
                            <span className="block font-medium text-sm truncate">{u.name}</span>
                            <span className="block text-xs text-muted-foreground">{ROLE_NL[u.role] || u.role}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                    <Button onClick={startGroup} disabled={groupSel.length < 2} className="w-full mt-3 bg-primary hover:bg-primary/90" data-testid="chat-group-start">
                      {tx("Start groepsgesprek")} {groupSel.length > 0 && `(${groupSel.length})`}
                    </Button>
                  </TabsContent>
                )}
              </>
            )}
          </Tabs>
        </DialogContent>
      </Dialog>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-sm" data-testid="chat-settings-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Flame className="h-4 w-4 text-orange-500" /> {tx("Vlam-drempel")}</DialogTitle>
            <DialogDescription>{tx("Na hoeveel minuten krijgt een onbeantwoord bericht een vlam-icoon?")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">{tx("Minuten")}</label>
            <Input type="number" min={1} max={1440} value={flameMinutes} onChange={(e) => setFlameMinutes(e.target.value)} data-testid="chat-flame-minutes-input" />
            <p className="text-xs text-muted-foreground">{tx("Geldt voor alle gesprekken (gast, woning en 1-op-1).")}</p>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setSettingsOpen(false)} data-testid="chat-settings-cancel">{tx("Annuleren")}</Button>
            <Button onClick={saveSettings} disabled={savingSettings || !flameMinutes} className="bg-primary hover:bg-primary/90" data-testid="chat-settings-save">
              {savingSettings ? <Loader2 className="h-4 w-4 animate-spin" /> : tx("Opslaan")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {isAdmin && thread?.channel_id && (
        <ChatMembersDialog channelId={thread.channel_id} isGroup={!!thread.group} open={membersOpen} onOpenChange={setMembersOpen}
          onSaved={(cid) => { const ch = { kind: "direct", id: cid || thread.channel_id }; loadConvos(); setActive(ch); loadThread(ch); }} />
      )}
    </div>
  );
}
